import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

interface PeerNameDisplayProps {
  name: string;
  onNameChange: (newName: string) => void;
}

export function PeerNameDisplay({ name, onNameChange }: PeerNameDisplayProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(name);
  }, [name]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditValue(name);
  };

  const handleSave = () => {
    const trimmedName = editValue.trim();
    if (trimmedName && trimmedName !== name) {
      onNameChange(trimmedName);
    } else {
      setEditValue(name);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-400">You:</span>
      
      {isEditing ? (
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          className="flex items-center gap-2"
        >
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className="px-3 py-1 bg-gray-800 border border-blue-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={30}
          />
        </motion.div>
      ) : (
        <button
          onClick={handleStartEdit}
          className="group flex items-center gap-2 px-3 py-1 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 hover:border-blue-500 rounded-lg transition-all"
        >
          <span className="text-sm font-medium text-white">{name}</span>
          <span className="text-xs text-gray-500 group-hover:text-blue-400 transition-colors">
            ✏️
          </span>
        </button>
      )}
    </div>
  );
}
